import React from "react";
import { useTranslation } from "react-i18next";
import { FailedIcon, DoneIcon, WarningIcon } from "../assets/registtyIcons/icons";
import Tooltip from "./Tooltip";

const TextComparisonDisplay = ({
  title1,
  title2,
  value1,
  value2,
  matchText = "matchingStatus",
  divClasses = "o-column o-column--gap-size_400",
  titleClasses = "t-bold_semi-bold u-width--min-width_300",
  spanStyle = "u-width--min-width_300",
}) => {
  const { t } = useTranslation("general");

  // Normalize text for comparison (remove whitespace, invisible chars, lowercase)
  const normalizeForComparison = (value) => {
    if (value == null || value === undefined) return "";
    return value
      .toString()
      .replace(/\s/g, "")
      .replace(/[，。,.、;；:：]/g, "")
      .replace(/[０-９]/g, (ch) =>
        String.fromCharCode(ch.charCodeAt(0) - 0xfee0)
      )
      .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
      .replace(/[\u202A-\u202E\u2066-\u2069]/g, "")
      .replace(/[\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180E]/g, "")
      .toLowerCase()
      .normalize("NFKC");
  };

  // Calculate match percentage
  const calculateMatchPercentage = (val1, val2) => {
    const norm1 = normalizeForComparison(val1);
    const norm2 = normalizeForComparison(val2);

    if (!norm1 || !norm2) return null;
    if (norm1 === norm2) return 100;

    const levenshteinDistance = (s1, s2) => {
      const matrix = [];
      for (let i = 0; i <= s2.length; i++) matrix[i] = [i];
      for (let j = 0; j <= s1.length; j++) matrix[0][j] = j;

      for (let i = 1; i <= s2.length; i++) {
        for (let j = 1; j <= s1.length; j++) {
          if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
            matrix[i][j] = matrix[i - 1][j - 1];
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j - 1] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j] + 1
            );
          }
        }
      }
      return matrix[s2.length][s1.length];
    };

    const distance = levenshteinDistance(norm1, norm2);
    const maxLength = Math.max(norm1.length, norm2.length);
    const similarity = ((maxLength - distance) / maxLength) * 100;
    return Math.round(similarity);
  };

  const getHighlightedText = (originalText, compareText, isFirst) => {
    if (!originalText) return "-";

    const original = originalText.toString();
    const normalized1 = normalizeForComparison(original);
    const normalized2 = normalizeForComparison(compareText);

    // ✅ If fully normalized strings match, show all green
    if (normalized1 === normalized2) {
      return (
        <span style={{ backgroundColor: "#c8e6c9", padding: "2px 0" }}>
          {original}
        </span>
      );
    }

    if (!normalized2) {
      return (
        <span style={{ backgroundColor: "#ffcccc", padding: "2px 0" }}>
          {original}
        </span>
      );
    }

    // Otherwise, do character-by-character comparison
    const highlightedChars = [];
    let normIndex = 0;

    for (let i = 0; i < original.length; i++) {
      const char = original[i];
      const isWhitespace = /\s/.test(char);

      if (isWhitespace) {
        highlightedChars.push(
          <span key={i} style={{ whiteSpace: "pre" }}>
            {char}
          </span>
        );
        continue;
      }

      const normalizedChar = normalizeForComparison(char);

      if (!normalizedChar) {
        highlightedChars.push(<span key={i}>{char}</span>);
        continue;
      }

      let backgroundColor = "#ffcccc";

      if (normIndex < normalized2.length) {
        const compareChar = normalized2[normIndex];

        if (normalizedChar === compareChar) {
          backgroundColor = "#c8e6c9";
        } else if (normalized2.includes(normalizedChar)) {
          backgroundColor = "#fff9c4";
        }
      }

      highlightedChars.push(
        <span
          key={i}
          style={{
            backgroundColor,
            padding: "2px 0",
            transition: "background-color 0.2s",
          }}
        >
          {char}
        </span>
      );

      normIndex++;
    }

    return <>{highlightedChars}</>;
  };

  const matchPercentage = calculateMatchPercentage(value1, value2);

  // Determine status configuration
  const getStatusConfig = (percent) => {
    if (percent === null) {
      return {
        icon: <FailedIcon width={20} height={20} />,
        textClass: "negative-value",
        text: t("notExistent"),
      };
    }
    if (percent >= 90) {
      return {
        icon: <DoneIcon width={20} height={20} />,
        textClass: "confirm-text",
        text: `${t("matchPercentage")} ${percent}%`,
      };
    }
    if (percent >= 0) {
      return {
        icon: <WarningIcon width={20} height={20} />,
        textClass: "warning-text",
        text: `${t("matchPercentage")} ${percent}%`,
      };
    }
    return {
      icon: <FailedIcon width={20} height={20} />,
      textClass: "error-text",
      text: `${t("matchPercentage")} ${percent}%`,
    };
  };

  const statusConfig = getStatusConfig(matchPercentage);

  return (
    <div className={divClasses}>
      {/* First Value with Highlighting */}
      <div className="o-column-row-switcher">
        <p className={`u-padding_0 u-margin_0 ${titleClasses}`}>{t(title1)}:</p>
        <span className="t-bold_semi-bold" style={{ lineHeight: "1.8" }}>
          {getHighlightedText(value1, value2, true)}
        </span>
      </div>

      {/* Second Value with Highlighting */}
      <div className="o-column-row-switcher">
        <p className={`u-padding_0 u-margin_0 ${titleClasses}`}>{t(title2)}:</p>
        <span className="t-bold_semi-bold" style={{ lineHeight: "1.8" }}>
          {getHighlightedText(value2, value1, false)}
        </span>
      </div>

      {/* Match Percentage Display */}
      <div className="o-column-row-switcher">
        <span className={spanStyle}>{t(matchText)}:</span>
        <div className="o-row o-row--gap-size_100 o-row--vertical-align_center">
          {statusConfig.icon}
          <span className={`t-body-text t-bold_bold ${statusConfig.textClass}`}>
            {statusConfig.text}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div
        className="o-row o-row--gap-size_200 o-row--vertical-align_center"
        style={{ fontSize: "0.85em", marginTop: "8px" }}
      >
        <Tooltip content={t("exactMatch")} position="top" maxWidth="200px">
          <span
            style={{
              display: "inline-block",
              width: "16px",
              height: "16px",
              backgroundColor: "#c8e6c9",
              borderRadius: "50%",
              cursor: "help",
            }}
          />
        </Tooltip>

        <Tooltip content={t("partialMatch")} position="top" maxWidth="200px">
          <span
            style={{
              display: "inline-block",
              width: "16px",
              height: "16px",
              backgroundColor: "#fff9c4",
              borderRadius: "50%",
              cursor: "help",
            }}
          />
        </Tooltip>

        <Tooltip content={t("notFound")} position="top" maxWidth="200px">
          <span
            style={{
              display: "inline-block",
              width: "16px",
              height: "16px",
              backgroundColor: "#ffcccc",
              borderRadius: "50%",
              cursor: "help",
            }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default TextComparisonDisplay;
